import React, { useEffect, useRef, useState } from 'react';
import { X, Camera, ScanLine } from 'lucide-react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useTheme } from '../../contexts/ThemeContext';
import { readScan } from '../../utils/scanPayload';

/**
 * Scan whatever is held up at the counter, and hand back what it was.
 *
 * ONE SCANNER, BOTH CODES. A player shows their profile code; somebody else
 * arrives holding a receipt with the order code on it. Asking the desk to pick
 * the right scanner first would be asking them to know the answer before
 * looking, so this reads both and the payload decides which it was.
 *
 * Both formats are enabled explicitly. The library defaults to QR only, so a
 * rectangular barcode on a receipt would simply never decode — the camera would
 * sit there looking like it was working. CODE_128 and PDF_417 are the horizontal
 * ones; AZTEC and DATA_MATRIX cost nothing to accept alongside them.
 *
 * Deliberately does no work of its own: the two scanners already in the app each
 * bake in what happens next — one marks attendance, one checks a class code —
 * which is why neither could be reused for a third purpose. This one decodes and
 * calls back, so the caller decides what a scan means.
 *
 * A camera can always be unavailable: denied permission, no device, an insecure
 * origin. That is not an error state to hide behind a spinner — the desk has
 * somebody waiting, so the message says plainly to type the name instead.
 */
const DeskScanModal = ({ isOpen, onClose, onScan }) => {
  const { theme } = useTheme();
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  // Guards against the camera firing the callback twice for one code, which it
  // does readily at 10fps — the second one would arrive after the modal closed.
  const doneRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    doneRef.current = false;
    setError(null);

    const scanner = new Html5Qrcode('desk-qr-reader', {
      formatsToSupport: [
        Html5QrcodeSupportedFormats.QR_CODE,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.PDF_417,
        Html5QrcodeSupportedFormats.AZTEC,
        Html5QrcodeSupportedFormats.DATA_MATRIX,
      ],
    });
    scannerRef.current = scanner;

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 240, height: 240 } },
      (text) => {
        if (doneRef.current) return;
        const payload = readScan(text);
        if (!payload) {
          // Named, because "invalid code" leaves somebody rescanning the same
          // thing. This says the code read fine and was not one of ours.
          setError('Read that, but it is not a player or order code.');
          return;
        }
        doneRef.current = true;
        onScan(payload);
      },
      // Per-frame decode failures are constant and meaningless while aiming.
      () => {},
    ).catch(() => setError('No camera available. Type the player\'s name instead.'));

    return () => {
      // stop() rejects if it never started; either way the element is going.
      scanner.stop().then(() => scanner.clear()).catch(() => {});
      scannerRef.current = null;
    };
  }, [isOpen, onScan]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className={`${theme.card} rounded-2xl w-full max-w-sm p-5`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`font-semibold ${theme.text} flex items-center gap-2`}>
            <ScanLine size={16} /> Scan a code
          </h3>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${theme.cardHover}`} aria-label="Close">
            <X size={16} className={theme.text} />
          </button>
        </div>

        <div id="desk-qr-reader" className="rounded-xl overflow-hidden" />

        <p className={`text-xs ${theme.textMuted} mt-3 flex items-start gap-1.5`}>
          <Camera size={12} className="mt-0.5 flex-shrink-0" />
          {error || 'A player\'s profile code, or the code on their receipt. Scanning only looks them up — nothing is charged by it.'}
        </p>
      </div>
    </div>
  );
};

export default DeskScanModal;
